import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Box } from "@mui/system";
import { List, Grid, ListSubheader } from "@mui/material";
import ContactPageOutlinedIcon from "@mui/icons-material/ContactPageOutlined";
import DatasetLinkedIcon from "@mui/icons-material/DatasetLinked";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import NotificationsIcon from "@mui/icons-material/Notifications";
import InventoryIcon from "@mui/icons-material/Inventory";
import DateRangeIcon from "@mui/icons-material/DateRange";
import ManageAccountsIcon from "@mui/icons-material/ManageAccounts";
import { AppState } from "../../../redux/store";
import { fontColor } from "../../../constants/ColorSets";
import {
  isCurrentAccountAdmin,
  isCurrentAccountOrgManagerOrAdmin,
  isKiwiRailUser,
} from "../AppAuthProvider";
import { TeamMembersState } from "../../../types/models/TeamMembersState";
import NavigationItem from "../../NavigationItem/NavigationItem";
import AppLogo from "../AppLogo";
import UserInfo from "../UserInfo";
import { LogoutDialog } from "../../LogoutDialog";
import { RouterConfigData } from "../../../types/models/RouterConfigData";
import { useLocation } from "react-router-dom";
import { useTeams } from "../../../hooks/useTeams";
import { RoleState } from "../../../types/models/RoleState";

interface SidebarContentProps {
  sidebarHidden?: boolean;
}

const defaultTeamConfig: RouterConfigData = {
  id: "teams",
  title: "Your Teams",
  type: "item",
  icon: <GroupsOutlinedIcon />,
  url: "/your-teams",
  alert: false,
  active: false,
};

const SidebarContent: React.FC<SidebarContentProps> = ({ sidebarHidden }) => {
  const [isOpenLogoutDialog, setIsOpenLogoutDialog] = useState<boolean>(false);
  const [teamConfig, setTeamConfig] = useState<RouterConfigData>(defaultTeamConfig);
  const teamState = useSelector<AppState, TeamMembersState>((state) => state.teamMembers);
  const roleState = useSelector<AppState, RoleState>((state) => state.role);
  const isOrgManagerOrAdmin = isCurrentAccountOrgManagerOrAdmin();
  const isAdmin = isCurrentAccountAdmin();
  const isKiwiRail = isKiwiRailUser();
  const location = useLocation();
  useTeams();

  const isAuthor = roleState.contentAuthor ? roleState.contentAuthor : false;

  useEffect(() => {
    const teamMenuActive = location.pathname.includes("your-teams");
    if (teamConfig.active != teamMenuActive) {
      setTeamConfig((prev) => ({
        ...prev,
        active: teamMenuActive,
      }));
    }
  }, [location.pathname]);

  useEffect(() => {
    const teamBadgeActive = (teamState.unrespondedMessageCount ?? 0) > 0;
    if (teamConfig.alert != teamBadgeActive)
      setTeamConfig((prev) => ({
        ...prev,
        alert: teamBadgeActive,
      }));
  }, [teamState.unrespondedMessageCount]);

  return (
    <>
      <Grid className={`${sidebarHidden ? "app-side-bar-collapse" : "app-side-bar"}`}>
        {!sidebarHidden ? (
          <>
            <Box
              sx={{
                position: "relative",
                paddingY: 3,
                paddingX: 2,
              }}
            >
              <AppLogo />
            </Box>
            <List
              sx={{
                position: "relative",
                padding: 0,
              }}
              component="div"
            >
              <NavigationItem
                item={{
                  id: "rules",
                  title: "Rules",
                  type: "item",
                  icon: <DescriptionOutlinedIcon />,
                  url: "/rules",
                }}
                level={0}
              />
              <NavigationItem
                item={{
                  id: "alerts",
                  title: "Alerts",
                  type: "item",
                  icon: <NotificationsIcon />,
                  url: "/alerts",
                  count:
                    teamState.myProgress?.alertCount != null &&
                    teamState.myProgress.acknowledgedCount != null
                      ? (teamState.myProgress.alertCount ?? 0) -
                        (teamState.myProgress.acknowledgedCount ?? 0)
                      : null,
                }}
                level={0}
              />
              {(isOrgManagerOrAdmin ||
                isKiwiRail ||
                (teamState.myTeams && teamState.myTeams.length > 0)) && (
                <>
                  <ListSubheader
                    sx={{ background: "transparent", color: fontColor.subTitleFontColor }}
                  >
                    Management
                  </ListSubheader>
                  <NavigationItem item={teamConfig} level={0} />
                </>
              )}
              {isOrgManagerOrAdmin && (
                <>
                  <NavigationItem
                    item={{
                      id: "all_teams",
                      title: "Manage Teams",
                      type: "item",
                      icon: <ContactPageOutlinedIcon />,
                      url: "/all-teams",
                    }}
                    level={0}
                  />
                  <NavigationItem
                    item={{
                      id: "userAdministration",
                      title: "User Admin",
                      type: "item",
                      icon: <ManageAccountsIcon />,
                      url: "/user-administration",
                    }}
                    level={0}
                  />
                </>
              )}
              {isAdmin && (
                <>
                  <ListSubheader
                    sx={{ background: "transparent", color: fontColor.subTitleFontColor }}
                  >
                    Admin
                  </ListSubheader>
                  <NavigationItem
                    item={{
                      id: "roleBundles",
                      title: "Role Bundles",
                      type: "item",
                      icon: <InventoryIcon />,
                      url: "/role-bundles",
                    }}
                    level={0}
                  />
                  <NavigationItem
                    item={{
                      id: "lto",
                      title: "LTO / Role Bundle",
                      type: "item",
                      icon: <DatasetLinkedIcon />,
                      url: "/lto-setup",
                    }}
                    level={0}
                  />
                  <NavigationItem
                    item={{
                      id: "ruleOfTheWeek",
                      title: "Rule of the Week",
                      type: "item",
                      icon: <DateRangeIcon />,
                      url: "/rules-of-the-week",
                    }}
                    level={0}
                  />
                </>
              )}
              {!isAdmin && isAuthor && (
                <>
                  <ListSubheader
                    sx={{ background: "transparent", color: fontColor.subTitleFontColor }}
                  >
                    Author
                  </ListSubheader>
                  <NavigationItem
                    item={{
                      id: "ruleOfTheWeek",
                      title: "Rule of the Week",
                      type: "item",
                      icon: <DateRangeIcon />,
                      url: "/rules-of-the-week",
                    }}
                    level={0}
                  />
                </>
              )}
            </List>
            <Box className="app-side-bar-footer">
              <Box
                sx={{
                  mx: 1.5,
                  mb: 1.5,
                }}
              >
                <UserInfo onLogoutRequest={() => setIsOpenLogoutDialog(true)} />
              </Box>
            </Box>
          </>
        ) : null}
      </Grid>
      <LogoutDialog open={isOpenLogoutDialog} onDeny={() => setIsOpenLogoutDialog(false)} />
    </>
  );
};

export default SidebarContent;
